import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { getMe, getSaved, addSaved, removeSaved, logout as apiLogout } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUserState] = useState(null);
  const [savedIds, setSavedIds] = useState([]);
  const [loading, setLoading] = useState(true);

  const refreshSaved = useCallback(async () => {
    try {
      const s = await getSaved();
      setSavedIds(s.map((p) => p.id));
    } catch {
      setSavedIds([]);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const u = await getMe();
      setUserState(u);
      setSavedIds(u.savedProjectIds || []);
    } catch {
      setUserState(null);
      setSavedIds([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const logout = useCallback(async () => {
    try { await apiLogout(); } catch (e) { console.error('Logout request failed', e); }
    setUserState(null);
    setSavedIds([]);
  }, []);

  const setUser = useCallback((u) => {
    setUserState(u);
    setSavedIds((u && u.savedProjectIds) || []);
  }, []);

  const isSaved = useCallback((id) => savedIds.includes(id), [savedIds]);

  const toggleSaved = useCallback(async (id) => {
    if (!user) return { ok: false, reason: 'auth' };
    const has = savedIds.includes(id);
    setSavedIds((prev) => (has ? prev.filter((x) => x !== id) : [...prev, id]));
    try {
      if (has) await removeSaved(id);
      else await addSaved(id);
    } catch {
      refreshSaved();
    }
    return { ok: true, saved: !has };
  }, [user, savedIds, refreshSaved]);

  const value = useMemo(
    () => ({ user, loading, checkAuth, login, logout, setUser, savedIds, isSaved, toggleSaved, refreshSaved }),
    [user, loading, checkAuth, login, logout, setUser, savedIds, isSaved, toggleSaved, refreshSaved]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
